import { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Outlet, useLocation } from 'react-router-dom';
import { Store, Globe, FileText, CheckCircle2, Star, ChevronDown } from 'lucide-react';
import { useCountry, COUNTRY_OPTIONS } from '@/hooks/useCountry';
import {
  visibleCategories,
  visibleAddons,
  visibleModuleCount,
} from '@/lib/modules-catalog';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { displayBasePrice, displayListPrice, type DisplayCurrency } from '@/lib/price-currency';
import { PassThroughNote } from '@/components/subscription/PassThroughNote';
import { authPageTitle } from '@/lib/auth-page-title';

export function AuthLayout() {
  const { country, isPakistan, setCountry } = useCountry();
  const { pathname } = useLocation();
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const { currency, setCurrency, baseUsd, addonUsd } = useDisplayCurrency(isPakistan);

  const categories = visibleCategories(isPakistan);
  const addons = visibleAddons(isPakistan);
  const moduleCount = visibleModuleCount(isPakistan);

  /* SEO meta tags — FBR mention only for Pakistan */
  useEffect(() => {
    // POS-I186: each auth page has its own title; the country wording stays in the description only
    document.title = authPageTitle(pathname);
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = isPakistan
      ? 'FBR-compliant POS built for Pakistani retailers — real-time FBR Digital Invoice submission, ' +
        'inventory management, double-entry accounting, restaurant management, loyalty program, ' +
        'and more. Starting PKR 999/month.'
      : 'All-in-one POS for retailers and restaurants — inventory management, double-entry accounting, ' +
        'restaurant management, loyalty program, and more. Starting PKR 999/month.';
    return () => { document.title = 'ClearRing'; };
  }, [isPakistan, pathname]);

  const heroTagline = isPakistan
    ? 'Built for Pakistan. FBR Compliant by Default.'
    : 'One platform to run your entire business.';

  const heroBlurb = isPakistan
    ? 'Run your entire business from one dashboard — automatic FBR Digital Invoice submission, real-time ' +
      'inventory, double-entry accounting, restaurant management, and reports your CA will love.'
    : 'Run your entire business from one dashboard — real-time inventory, double-entry accounting, ' +
      'restaurant management, loyalty, and reports your accountant will love.';

  const heroPills = isPakistan
    ? ['POS Terminal', 'FBR DI All 28 Scenarios', 'Restaurant Module', 'Accounting & Reports', 'Loyalty Program', 'Multi-Location', 'Offline Mode']
    : ['POS Terminal', 'Restaurant Module', 'Accounting & Reports', 'Loyalty Program', 'Multi-Location', 'Offline Mode'];

  const trustPoints = [
    'No credit card required',
    'Setup in under 5 minutes',
    isPakistan ? 'Data stays in Pakistan' : 'Your data, fully secured',
    'Cancel anytime',
  ];

  return (
    <div className="min-h-screen bg-background">

      {/* ═══ HERO HEADER — full-width dark gradient ═══ */}
      <header
        className="relative overflow-hidden bg-[#0a0a1a] text-primary-foreground"
        aria-label="ClearRing — product overview"
      >
        {/* Aurora blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -top-1/2 -start-1/4 h-[600px] w-[600px] rounded-full bg-primary/25 blur-[100px] animate-drift" />
          <div className="absolute -bottom-1/3 end-0 h-[500px] w-[500px] rounded-full bg-primary/20 blur-[90px] animate-drift-reverse" />
          <div className="absolute top-1/2 left-1/2 h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-[60px] animate-morph" />
        </div>
        {/* Faint grid */}
        <div
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '48px 48px' }}
          aria-hidden="true"
        />

        <div className="relative z-10 px-6 py-8 xl:py-10">
          {/* ── Logo row ── */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6 animate-fade-in-up">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 shadow-lg shadow-primary/30 ring-1 ring-background/10">
                <Store className="h-5 w-5" aria-hidden="true" />
              </div>
              <div>
                <p className="text-lg font-bold tracking-tight leading-none">ClearRing</p>
                <p className="text-[10px] tracking-widest uppercase text-primary-foreground/60 mt-0.5">Enterprise Platform</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Country selector */}
              <div className="inline-flex items-center gap-1.5 rounded-full border border-background/10 bg-card/5 px-3 py-1 text-xs text-primary-foreground/70 backdrop-blur-sm">
                <Globe className="h-3.5 w-3.5 text-primary-foreground/70" aria-hidden="true" />
                <Dropdown
                  value={country}
                  onChange={(e) => setCountry(e.value)}
                  options={COUNTRY_OPTIONS.map((c) => ({ label: c.label, value: c.code }))}
                  optionLabel="label"
                  optionValue="value"
                  aria-label="Select your country"
                  className="p-dropdown-sm p-dropdown-on-dark text-xs font-medium"
                />
              </div>

              {/* Live badge — Pakistan only */}
              {isPakistan && (
                <div className="inline-flex items-center gap-2 rounded-full border border-background/10 bg-card/5 px-3 py-1 text-xs font-medium text-primary-foreground backdrop-blur-sm">
                  <span className="relative flex h-2 w-2" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                  </span>
                  FBR Integration Active
                </div>
              )}
            </div>
          </div>

          {/* ── Headline + pills ── */}
          <div className="animate-fade-in-up stagger-2">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary-foreground/50 mb-2">
              {heroTagline}
            </p>
            <h1 className="text-3xl xl:text-4xl font-bold leading-[1.1] tracking-tight mb-3">
              Sell Smarter.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary to-primary/80 animate-gradient-slow">
                {isPakistan ? 'Comply Automatically.' : 'Grow Faster.'}
              </span>
            </h1>
            <p className="text-sm text-primary-foreground/70 mb-5 max-w-2xl leading-relaxed">
              {heroBlurb}{' '}
              Starting at <span className="text-primary-foreground font-semibold">PKR 999/month</span>.
            </p>
            {/* Feature pills */}
            <div className="flex flex-wrap gap-2" role="list" aria-label="Key features">
              {heroPills.map(pill => (
                <span
                  key={pill}
                  role="listitem"
                  className="inline-flex items-center gap-1 rounded-full border border-background/10 bg-card/[0.06] px-3 py-1 text-xs font-medium text-primary-foreground/70"
                >
                  <CheckCircle2 className="h-3 w-3 text-primary-foreground/70 shrink-0" aria-hidden="true" />
                  {pill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* ═══ MAIN — two-column layout below hero ═══ */}
      <main className="w-full px-4 sm:px-6 xl:px-10 py-8 lg:grid lg:grid-cols-5 lg:gap-10 xl:gap-14">

        {/* ── LEFT: All modules + pricing ── */}
        <div className="lg:col-span-3 space-y-8 mb-10 lg:mb-0">

          {/* Module catalogue — grouped + expandable */}
          <section aria-labelledby="modules-heading">
            <button
              type="button"
              onClick={() => setFeaturesOpen((o) => !o)}
              aria-expanded={featuresOpen}
              aria-controls="feature-sections"
              className="flex w-full items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3 text-start transition-colors hover:border-primary/30 lg:cursor-default lg:hover:border-border"
            >
              <h2 id="modules-heading" className="text-xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
                Everything Your Business Needs — {moduleCount} Modules
              </h2>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform lg:hidden ${featuresOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>

            <div
              id="feature-sections"
              className={`mt-4 space-y-6 ${featuresOpen ? 'block' : 'hidden'} lg:block`}
            >
              {categories.map((cat) => (
                <div key={cat.id}>
                  <h3 className="mb-2 text-[11px] font-semibold tracking-[0.12em] uppercase text-primary-text/80">
                    {cat.label}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {cat.items.map((mod) => {
                      const Icon = mod.icon;
                      return (
                        <article
                          key={mod.key}
                          className="group relative rounded-xl border border-border bg-card p-3 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 hover:border-primary/30"
                        >
                          <div className={`mb-2 flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br ${mod.color} ring-1 ring-border/50`}>
                            <Icon className="h-3.5 w-3.5 text-primary-foreground" aria-hidden="true" />
                          </div>
                          <h4 className="text-[11px] font-semibold text-foreground leading-tight">{mod.label}</h4>
                          <p className="mt-1 text-[10px] leading-snug text-muted-foreground line-clamp-2 group-hover:text-foreground/70 transition-colors">{mod.desc}</p>
                        </article>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Pricing */}
          <section
            aria-labelledby="pricing-heading"
            className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/10 p-5"
          >
            <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
              <div>
                <h2 id="pricing-heading" className="text-xs font-semibold tracking-[0.15em] uppercase text-primary-text mb-1">
                  Simple, Transparent Pricing
                </h2>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold text-foreground">{displayBasePrice(999, currency, baseUsd)}</span>
                  <span className="text-sm text-muted-foreground">/month</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">Base plan — pay only for modules you use</p>
              </div>
              <div className="flex items-center gap-2">
                <div role="group" aria-label="Price currency" className="inline-flex rounded-md border border-border overflow-hidden text-[10px] font-semibold">
                  {(['PKR', 'USD'] as DisplayCurrency[]).map((c) => (
                    <button
                      key={c}
                      type="button"
                      aria-pressed={currency === c}
                      onClick={() => setCurrency(c)}
                      className={`px-2 py-1 ${currency === c ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-0.5 text-warning-text" aria-label="5 star rating">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-warning" aria-hidden="true" />)}
                </div>
              </div>
            </div>

            <ul className="space-y-1.5 mb-4">
              {['POS Sales Terminal', '50 Products included', '1 User + 1 Location', 'Customer Management', 'Basic Reports'].map(item => (
                <li key={item} className="flex items-center gap-2 text-xs text-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 text-success-text shrink-0" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>

            <div className="h-px w-full bg-border mb-4" />

            <p className="text-xs font-medium text-muted-foreground mb-2.5">Add-on modules (monthly):</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {addons.map(a => (
                <div key={a.key} className="flex items-center justify-between rounded-lg bg-primary-foreground/5 border border-border px-2.5 py-1.5">
                  <span className="text-[10px] text-muted-foreground leading-tight">{a.label}{a.passThrough && <span className="text-foreground">*</span>}</span>
                  <span className="text-[10px] font-semibold text-primary-text shrink-0 ms-1">+{displayListPrice(a.price, currency, addonUsd[a.key])}</span>
                </div>
              ))}
            </div>
            {addons.some(a => a.passThrough) && <PassThroughNote marker className="mt-2.5" />}
          </section>

          {/* Trust signals */}
          <ul className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {trustPoints.map(point => (
              <li key={point} className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 text-success-text shrink-0" aria-hidden="true" />
                {point}
              </li>
            ))}
          </ul>

          {/* FBR compliance note — Pakistan only */}
          {isPakistan && (
            <div className="flex items-center gap-2 rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm text-success-text">
              <FileText className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="text-xs">FBR Compliant — All 28 invoice scenarios supported (SN001–SN028), both STAGING and LIVE modes</span>
            </div>
          )}

          {/* Footer note */}
          <p className="text-[11px] text-muted-foreground/60 text-center pb-2">
            © {new Date().getFullYear()} ClearRing — IT Stack &nbsp;·&nbsp; All systems operational
          </p>
        </div>

        {/* ── RIGHT: Login / Register form ── */}
        <div className="lg:col-span-2">
          <div className="sticky top-8">
            {/* Mobile logo (hidden on desktop) */}
            <div className="mb-6 text-center lg:hidden">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/30 animate-gradient">
                <Store className="h-8 w-8" aria-hidden="true" />
              </div>
              <p className="text-xl font-bold tracking-tight bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                ClearRing
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{heroTagline}</p>
            </div>
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
